// Living style guide at /design-system. Internal reference (noindex) —
// renders the design tokens straight from theme.css via the Tailwind
// token classes, so the swatches reflect the real values and flip with
// the theme toggle. When a color looks wrong here, fix it in theme.css.

import { Link } from "react-router-dom";
import { Head } from "vite-react-ssg";

import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { SeverityBadge } from "@/components/ui/SeverityBadge";
import { ThemeToggle } from "@/components/ui/ThemeToggle";

// Token name (matches --color-<name> in theme.css) + whether it's a
// surface (show as a filled block) or a text/line token.
const COLOR_GROUPS: { group: string; tokens: string[] }[] = [
  { group: "Backgrounds", tokens: ["bg", "bg-deep", "bg2", "bg3", "card"] },
  { group: "Borders", tokens: ["stroke", "stroke-hv"] },
  { group: "Text", tokens: ["content", "muted", "dim"] },
  { group: "Accent", tokens: ["accent", "accent-bright", "accent-dark", "accent-bg"] },
  {
    group: "Semantic",
    tokens: [
      "success", "success-bg",
      "danger", "danger-bg",
      "critical", "critical-bg",
      "warning", "warning-bg",
      "info", "info-bg",
      "brand", "brand-bg",
    ],
  },
];

function Swatch({ token }: { token: string }) {
  return (
    <div className="flex flex-col gap-1">
      <div
        className="h-14 rounded border border-stroke"
        style={{ background: `rgb(var(--color-${token}))` }}
      />
      <code className="font-mono text-[10px] text-muted">--color-{token}</code>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-12">
      <h2 className="font-display text-[20px] font-bold text-content mb-4 pb-2 border-b border-stroke">
        {title}
      </h2>
      {children}
    </section>
  );
}

export function DesignSystemPage() {
  return (
    <div className="bg-bg min-h-screen text-content">
      <Head>
        <title>Design System — TrueHacking (internal)</title>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <header className="px-6 py-4 flex items-center justify-between border-b border-stroke">
        <Link to="/" className="font-display text-[16px] font-bold text-accent tracking-[0.12em] no-underline">
          TrueHacking · Design System
        </Link>
        <ThemeToggle />
      </header>

      <main className="max-w-[1000px] mx-auto px-6 py-10">
        <p className="font-mono text-[11px] text-muted mb-10 leading-[1.6]">
          Living reference for the design tokens. Every color comes from{" "}
          <code className="text-accent">src/styles/theme.css</code>. Toggle the
          theme to preview light/dark. Edit colors there — never hardcode hex.
        </p>

        <Block title="Colors">
          {COLOR_GROUPS.map((g) => (
            <div key={g.group} className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-dim mb-2">
                {g.group}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                {g.tokens.map((tk) => (
                  <Swatch key={tk} token={tk} />
                ))}
              </div>
            </div>
          ))}
        </Block>

        <Block title="Typography">
          <div className="space-y-3">
            <div className="font-display text-[36px] font-bold text-content">
              Display — Rajdhani 36/700
            </div>
            <div className="font-sans text-[16px] text-content">
              Sans — DM Sans 16/400. The quick brown fox jumps over the lazy dog.
            </div>
            <div className="font-mono text-[13px] text-muted">
              Mono — JetBrains Mono 13. const recon = await scan(target);
            </div>
          </div>
        </Block>

        <Block title="Buttons">
          <div className="flex flex-wrap gap-3 items-center">
            <Button variant="primary">Primary</Button>
            <Button variant="default">Default</Button>
            <Button variant="danger">Danger</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="primary" small>
              Small
            </Button>
          </div>
        </Block>

        <Block title="Badges & severity">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            {["critical", "high", "medium", "low", "informational"].map((s) => (
              <SeverityBadge key={s} severity={s} />
            ))}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Badge color="rgb(var(--color-accent))" bg="rgb(var(--color-accent-bg))">
              accent
            </Badge>
            <Badge color="rgb(var(--color-success))" bg="rgb(var(--color-success-bg))">
              success
            </Badge>
            <Badge color="rgb(var(--color-info))" bg="rgb(var(--color-info-bg))">
              info
            </Badge>
            <Badge color="rgb(var(--color-brand))" bg="rgb(var(--color-brand-bg))">
              brand
            </Badge>
          </div>
        </Block>
      </main>
    </div>
  );
}
